import { useEffect, useState } from 'react';
import { api, UnauthorizedError, type ManagedUser, type UserRole } from '../api';

type Props = {
  myEmail: string;
  onClose: () => void;
  onSessionExpired: () => void;
};

export function Users({ myEmail, onClose, onSessionExpired }: Props) {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<UserRole>('member');
  const [skipEmail, setSkipEmail] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .listUsers()
      .then((r) => setUsers(r.data))
      .catch((e) => {
        if (e instanceof UnauthorizedError) onSessionExpired();
        else setError(String(e.message || e));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitInvite = async (email: string, reactivate: boolean) => {
    const r = await api.inviteUser({ email, role: newRole, skip_email: skipEmail, reactivate });
    setUsers((us) => [r.data, ...us.filter((u) => u.email !== r.data.email)]);
    setNewEmail('');
    if (skipEmail) {
      setNotice(
        reactivate
          ? `${email} reativado (e-mail não enviado).`
          : `${email} adicionado (e-mail não enviado).`
      );
    } else if (r.email_sent) {
      setNotice(reactivate ? `${email} reativado e convite reenviado.` : `Convite enviado para ${email}.`);
    } else {
      setError(`Usuário criado, mas falha ao enviar e-mail: ${r.email_error || 'erro desconhecido'}`);
    }
  };

  const onInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      await submitInvite(email, false);
    } catch (e: any) {
      if (e instanceof UnauthorizedError) {
        onSessionExpired();
      } else if (e?.message === 'user_revoked') {
        if (confirm(`${email} foi revogado anteriormente. Reativar agora?`)) {
          try {
            await submitInvite(email, true);
          } catch (e2: any) {
            if (e2 instanceof UnauthorizedError) onSessionExpired();
            else setError(String(e2.message || e2));
          }
        }
      } else {
        setError(String(e.message || e));
      }
    } finally {
      setCreating(false);
    }
  };

  const onChangeRole = async (email: string, role: UserRole) => {
    try {
      await api.changeUserRole(email, role);
      setUsers((us) => us.map((u) => (u.email === email ? { ...u, role } : u)));
    } catch (e: any) {
      if (e instanceof UnauthorizedError) onSessionExpired();
      else setError(String(e.message || e));
    }
  };

  const onRemove = async (email: string) => {
    if (!confirm(`Remover ${email}? Perde acesso imediatamente e todas as service keys são revogadas.`)) return;
    try {
      await api.removeUser(email);
      setUsers((us) => us.filter((u) => u.email !== email));
    } catch (e: any) {
      if (e instanceof UnauthorizedError) onSessionExpired();
      else setError(String(e.message || e));
    }
  };

  const onResend = async (email: string) => {
    setError(null);
    setNotice(null);
    try {
      await api.resendInvite(email);
      setNotice(`Convite reenviado para ${email}.`);
    } catch (e: any) {
      if (e instanceof UnauthorizedError) onSessionExpired();
      else setError(String(e.message || e));
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="users-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-head">
          <div className="modal-title">Usuários</div>
          <button className="btn" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        <p className="modal-sub">
          Acesso invite-only. Apenas e-mails desta lista podem entrar no dashboard. Convites são enviados via AWS SES.
        </p>

        <form className="key-form" onSubmit={onInvite} style={{ flexWrap: 'wrap' }}>
          <input
            type="email"
            placeholder="email@exemplo.com (gmail, bix-tech, etc.)"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            data-testid="invite-email"
          />
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as UserRole)}
            data-testid="invite-role"
          >
            <option value="member">Membro</option>
            <option value="admin">Admin</option>
          </select>
          <button
            className="btn primary"
            type="submit"
            disabled={creating || !newEmail.trim()}
            data-testid="invite-submit"
          >
            {creating ? 'Convidando…' : 'Convidar'}
          </button>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--muted)', fontSize: 12 }}
          >
            <input
              type="checkbox"
              checked={skipEmail}
              onChange={(e) => setSkipEmail(e.target.checked)}
              data-testid="invite-skip-email"
            />
            não enviar e-mail
          </label>
        </form>

        {error && (
          <div className="error" data-testid="users-error">
            {error}
          </div>
        )}
        {notice && (
          <div className="users-notice" data-testid="users-notice">
            {notice}
          </div>
        )}

        <div className="key-list" data-testid="user-list">
          {loading && <div className="muted">carregando…</div>}
          {!loading && users.length === 0 && <div className="muted">Nenhum usuário ainda.</div>}
          {!loading &&
            users.map((u) => {
              const isSelf = u.email === myEmail;
              return (
                <div key={u.email} className="key-row" data-testid={`user-row-${u.email}`}>
                  <div className="key-meta">
                    <div className="key-name">
                      {u.name || u.email}
                      {u.role === 'admin' && <span className="role-badge">admin</span>}
                      {isSelf && <span className="self-badge">você</span>}
                    </div>
                    <div className="key-sub">
                      <code>{u.email}</code>
                      <span>·</span>
                      <span>convidado {formatTs(u.invited_at)}</span>
                      <span>·</span>
                      <span>último login {u.last_login_at ? formatTs(u.last_login_at) : 'nunca'}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {!isSelf && (
                      <select
                        value={u.role}
                        onChange={(e) => onChangeRole(u.email, e.target.value as UserRole)}
                        data-testid={`user-role-${u.email}`}
                      >
                        <option value="member">Membro</option>
                        <option value="admin">Admin</option>
                      </select>
                    )}
                    {!u.last_login_at && (
                      <button
                        className="btn"
                        onClick={() => onResend(u.email)}
                        data-testid={`user-resend-${u.email}`}
                      >
                        Reenviar
                      </button>
                    )}
                    {!isSelf && (
                      <button
                        className="btn danger"
                        onClick={() => onRemove(u.email)}
                        data-testid={`user-remove-${u.email}`}
                      >
                        Remover
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

function formatTs(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}
